import { CASE_001, characterById } from '../case/case001';
import type { CaseOutcome, CounselProfile } from '../game/types';

export type ShareCardVariant = 'verdict' | 'record' | 'truth';

const VARIANTS: Record<ShareCardVariant, {
  kicker: string;
  portrait: string;
  accent: string;
  headline: (outcome: CaseOutcome) => string;
  subline: (outcome: CaseOutcome) => string;
}> = {
  verdict: {
    kicker: 'TRIBUNAL FINDING / CASE RESOLVED',
    portrait: characterById.lyra.portrait,
    accent: '#cda15f',
    headline: (outcome) => outcome.verdict,
    subline: (outcome) => `Lyra Vale · ${outcome.clientProtected ? 'client protected' : 'status at risk'}`,
  },
  record: {
    kicker: 'VERIFIED COUNSEL RECORD',
    portrait: characterById.judge.portrait,
    accent: '#78b3bd',
    headline: (outcome) => outcome.routeTitle,
    subline: (outcome) => `${outcome.evidenceFound}/${CASE_001.evidence.length} records · ${outcome.contradictions}/${CASE_001.statements.length} contradictions`,
  },
  truth: {
    kicker: 'WORLD TRUTH / ARCHIVE RESTORED',
    portrait: characterById.orin.portrait,
    accent: '#a995d5',
    headline: (outcome) => outcome.truthFound ? 'THE SEVEN MINUTES' : 'A PARTIAL ECHO',
    subline: (outcome) => outcome.truthFound ? 'Hidden timeline recovered in full' : 'Some of Archive Nine remains unresolved',
  },
};

function imageFrom(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = reject;
    image.src = src;
  });
}

function roundedRect(
  context: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number,
) {
  context.beginPath();
  context.roundRect(x, y, width, height, radius);
  context.closePath();
}

function fitText(
  context: CanvasRenderingContext2D,
  text: string,
  maxWidth: number,
  startSize: number,
  family = '"Cormorant Garamond"',
) {
  let size = startSize;
  do {
    context.font = `600 ${size}px ${family}`;
    size -= 2;
  } while (size > 34 && context.measureText(text).width > maxWidth);
}

function drawGlyph(context: CanvasRenderingContext2D, profile: CounselProfile, x: number, y: number) {
  context.save();
  context.translate(x, y);
  context.strokeStyle = profile.accent;
  context.fillStyle = 'rgba(8,16,19,.72)';
  context.lineWidth = 3;
  context.beginPath();
  context.arc(0, 0, 38, 0, Math.PI * 2);
  context.fill();
  context.stroke();
  context.rotate(Math.PI / 4);
  context.strokeRect(-17, -17, 34, 34);
  context.rotate(-Math.PI / 4);
  context.beginPath();
  if (profile.avatar === 'cipher') {
    context.arc(0, 0, 13, 0, Math.PI * 1.55);
  } else if (profile.avatar === 'oracle') {
    context.ellipse(0, 0, 21, 10, 0, 0, Math.PI * 2);
    context.moveTo(6, 0);
    context.arc(0, 0, 6, 0, Math.PI * 2);
  } else if (profile.avatar === 'vector') {
    context.moveTo(-20, 9);
    context.lineTo(0, -21);
    context.lineTo(20, 9);
    context.closePath();
  } else {
    for (let index = 0; index < 8; index += 1) {
      const angle = (index / 8) * Math.PI * 2;
      context.moveTo(Math.cos(angle) * 8, Math.sin(angle) * 8);
      context.lineTo(Math.cos(angle) * 22, Math.sin(angle) * 22);
    }
  }
  context.stroke();
  context.restore();
}

export async function createShareCard(
  outcome: CaseOutcome,
  profile: CounselProfile,
  variant: ShareCardVariant = 'verdict',
): Promise<Blob> {
  await document.fonts.ready;
  const design = VARIANTS[variant];
  const canvas = document.createElement('canvas');
  canvas.width = 1200;
  canvas.height = 630;
  const context = canvas.getContext('2d');
  if (!context) throw new Error('Canvas unavailable');

  const background = context.createLinearGradient(0, 0, 1200, 630);
  background.addColorStop(0, '#060d10');
  background.addColorStop(0.56, '#122126');
  background.addColorStop(1, variant === 'truth' ? '#201c31' : '#39281c');
  context.fillStyle = background;
  context.fillRect(0, 0, 1200, 630);

  const portrait = await imageFrom(design.portrait);
  context.save();
  context.rect(758, 0, 442, 630);
  context.clip();
  const ratio = Math.max(442 / portrait.width, 630 / portrait.height);
  const width = portrait.width * ratio;
  const height = portrait.height * ratio;
  context.globalAlpha = 0.77;
  context.drawImage(portrait, 758 + (442 - width) / 2, (630 - height) / 2, width, height);
  const portraitWash = context.createLinearGradient(740, 0, 1120, 0);
  portraitWash.addColorStop(0, '#091114');
  portraitWash.addColorStop(0.58, 'rgba(9,17,20,.25)');
  portraitWash.addColorStop(1, 'rgba(9,17,20,0)');
  context.globalAlpha = 1;
  context.fillStyle = portraitWash;
  context.fillRect(720, 0, 480, 630);
  context.restore();

  context.strokeStyle = 'rgba(207,169,105,.48)';
  context.lineWidth = 2;
  context.strokeRect(31, 31, 1138, 568);
  context.strokeStyle = 'rgba(207,169,105,.13)';
  context.strokeRect(44, 44, 1112, 542);

  context.fillStyle = design.accent;
  context.font = '600 17px Manrope';
  context.letterSpacing = '4px';
  context.fillText(`HŌTEI  /  ${design.kicker}`, 76, 87);

  context.fillStyle = '#e9e2d7';
  context.font = '500 40px "Cormorant Garamond"';
  context.letterSpacing = '0px';
  context.fillText(CASE_001.title, 76, 139);

  fitText(context, design.headline(outcome), 635, variant === 'verdict' ? 76 : 65);
  context.fillStyle = '#f1eadf';
  context.fillText(design.headline(outcome), 74, 252);
  context.fillStyle = '#91a5a9';
  context.font = '500 18px Manrope';
  context.fillText(design.subline(outcome), 78, 286);

  roundedRect(context, 76, 323, 128, 132, 8);
  context.fillStyle = 'rgba(208,163,93,.11)';
  context.fill();
  context.strokeStyle = design.accent;
  context.stroke();
  context.fillStyle = '#93a6aa';
  context.font = '600 15px Manrope';
  context.fillText('RANK', 117, 354);
  context.fillStyle = '#f1eadf';
  context.font = '600 72px "Cormorant Garamond"';
  context.fillText(outcome.rank, 118, 430);

  const stats = [
    ['CASE SCORE', `${outcome.score} / 100`],
    ['TRUTH', outcome.truthFound ? 'RECOVERED' : 'PARTIAL'],
    ['YURAGI REPLAY', outcome.yuragiReplayVerified ? 'VERIFIED' : 'UNVERIFIED'],
  ];
  stats.forEach(([label, value], index) => {
    const x = 238 + index * 150;
    context.fillStyle = '#75898d';
    context.font = '600 13px Manrope';
    context.fillText(label, x, 353);
    context.fillStyle = index === 2 ? profile.accent : '#e7e0d5';
    context.font = '600 21px Manrope';
    context.fillText(value, x, 386);
  });

  context.strokeStyle = 'rgba(255,255,255,.1)';
  context.beginPath();
  context.moveTo(238, 414);
  context.lineTo(680, 414);
  context.stroke();

  drawGlyph(context, profile, 116, 522);
  context.fillStyle = profile.accent;
  context.font = '600 14px Manrope';
  context.letterSpacing = '2px';
  context.fillText(profile.callsign, 170, 506);
  context.fillStyle = '#e8e1d6';
  context.font = '500 21px "Cormorant Garamond"';
  context.letterSpacing = '0px';
  context.fillText(profile.name || 'Anonymous Counsel', 170, 532);
  context.fillStyle = '#6f8387';
  context.font = '500 12px Manrope';
  context.fillText(`${profile.counselId}  ·  LEVEL ${profile.level.toString().padStart(2, '0')}`, 170, 554);

  context.fillStyle = '#718488';
  context.font = '500 12px Manrope';
  context.fillText(`VERIFIABLE RESULT  ${outcome.resultHash}`, 758, 542);
  context.fillText(`YURAGI PROOF  ${outcome.yuragiProofHash.slice(0, 18).toUpperCase()}`, 758, 562);
  context.fillStyle = design.accent;
  context.fillText('YOUR THEORY BECOMES THE RECORD.', 758, 584);

  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('Card render failed'))), 'image/png');
  });
}

export async function downloadShareCard(
  outcome: CaseOutcome,
  profile: CounselProfile,
  variant: ShareCardVariant = 'verdict',
) {
  const blob = await createShareCard(outcome, profile, variant);
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = `hotei-${variant}-${outcome.resultHash}.png`;
  anchor.click();
  window.setTimeout(() => URL.revokeObjectURL(url), 500);
}

export async function shareResult(
  outcome: CaseOutcome,
  profile: CounselProfile,
  variant: ShareCardVariant = 'verdict',
) {
  const text = `${profile.callsign} defended Lyra Vale in HŌTEI — ${outcome.verdict}, rank ${outcome.rank}, ${outcome.score}/100. Yuragi replay ${outcome.yuragiReplayVerified ? 'verified' : 'unverified'} · result ${outcome.resultHash}.`;
  const blob = await createShareCard(outcome, profile, variant);
  const file = new File([blob], `hotei-${variant}-${outcome.resultHash}.png`, { type: 'image/png' });
  if (navigator.share && navigator.canShare?.({ files: [file] })) {
    await navigator.share({ title: 'HŌTEI counsel record', text, files: [file] });
    return 'shared';
  }
  await navigator.clipboard.writeText(text);
  return 'copied';
}
